/**
 * A size the item master tags one tier while the base stands it with another tier's packs is a
 * question for the merchant, never a silent re-bucket.
 *
 * Inside a brand block whose shelf rows are size tiers (cdt2 is the size tier, or a form row the
 * base stacks by size), the products of one pack size and one tier are read against the rest of
 * the block: where their base shelves sit wholly within another tier's run of shelves and not
 * within their own tier's, the base merchandises them as that tier. Tide Simply's 92 fl oz
 * bottles on base 210 are tagged Medium and stand on the top shelf with its Small packs; its
 * other Medium packs stand on shelves 2-5. Yes re-tags those products' size tier through
 * `attrOverrides` (the split-off offers' fix), so the release ships them in that tier's row; No
 * is kept like a split-off decline. No threshold: a run of shelves is the shelves a tier's other
 * products stand on, top to bottom; the host tier is most of each shelf the pack size stands on;
 * and the pack size is its tier's borderline one (no pack of its form and tier lies further
 * toward the host), so a pack is never moved past one of its own tier left behind.
 */
import type { CollapseResult } from "./collapse4";
import { COMPOUND_JOIN } from "./collapse4";
import {
  detailText,
  englishFlagTemplate,
  phrase,
  phraseText,
  type FlagPhrase,
  type FlagWords,
} from "./flagWords";
import { stackedBySize } from "./rowLines";
import { FORM_ATTR, SIZE_ATTR, SIZE_TIER_ATTR, sizeTierRank } from "./sizeTiers";
import {
  type Flag,
  PATH_SEPARATOR,
  type StudioDraft,
  type StudioItem,
  effectiveAttrs,
  isPlaceholderValue,
} from "./studioTypes";

export const SIZE_TIER_QUESTION_PREFIX = "size-tier:";

/** The flag id of a size-tier question: one per brand block and pack size. */
export const sizeTierQuestionId = (column: string, size: string, to: string): string =>
  `${SIZE_TIER_QUESTION_PREFIX}${column}:${size}:${to}`;

const valueOf = (item: StudioItem, attr: string): string => String(item.attrs[attr] ?? "").trim();

const shelvesOf = (items: readonly StudioItem[]): number[] =>
  [...new Set(items.flatMap((item) => item.placements.map((placement) => placement.shelf)))].sort(
    (a, b) => a - b,
  );

const shelvesWords = (shelves: readonly number[]): FlagPhrase => {
  if (shelves.length === 1) return phrase("shelves", { shelves: shelves[0] }, 1);
  const first = shelves[0];
  const last = shelves[shelves.length - 1];
  return last - first === shelves.length - 1
    ? phrase("shelfRange", { from: first, to: last })
    : phrase("shelves", { shelves: shelves.join(", ") }, shelves.length);
};

/** The pack size as the merchant reads it: the item master's size, with the unit the product
 *  name gives it when the size is a bare number ("92" in "..., 92 fl oz" reads "92 fl oz"). Null
 *  when the name gives no unit for it: a bare "36" means nothing to a merchant. */
const packSizeLabel = (item: StudioItem): string | null => {
  const size = valueOf(item, SIZE_ATTR);
  if (!/^\d+(?:\.\d+)?$/.test(size)) return size;
  const escaped = size.replace(".", "\\.");
  const unit = new RegExp(
    `(?:^|[^\\d.])${escaped}\\s*(fl\\.?\\s*oz|oz|ct|count|lbs?|ml|l|g|kg|gal|pk)\\b`,
    "i",
  ).exec(item.name ?? "");
  return unit ? `${size} ${unit[1].toLowerCase()}` : null;
};

const packSizeOf = (item: StudioItem): number | null => {
  const match = /^\s*(\d+(?:\.\d+)?)/.exec(valueOf(item, SIZE_ATTR));
  return match ? Number(match[1]) : null;
};

/** The products of `items` grouped one pack size and one tier each. */
const sizeGroupsOf = (items: readonly StudioItem[]): StudioItem[][] => {
  const groups = new Map<string, StudioItem[]>();
  for (const item of items) {
    const size = valueOf(item, SIZE_ATTR);
    if (size === "" || isPlaceholderValue(size)) continue;
    const key = `${valueOf(item, SIZE_TIER_ATTR)}\u001f${size}`;
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }
  return [...groups.values()];
};

interface TierOffer {
  skuIds: string[];
  /** The item master's size, as the id names it. */
  size: string;
  /** How the card names the products after the brand ("92 fl oz"), else null. */
  label: string | null;
  /** The product's own name, for a single product whose size has no unit. */
  name: string | null;
  from: string;
  to: string;
  shelves: number[];
  ownShelves: number[];
}

/** Inside one set of size rows: each pack size the base stands wholly within one other tier's
 *  run of shelves and not within its own tier's. */
const offersIn = (items: readonly StudioItem[]): TierOffer[] => {
  const tiered = items.filter((item) => {
    const tier = valueOf(item, SIZE_TIER_ATTR);
    return tier !== "" && !isPlaceholderValue(tier) && item.placements.length > 0;
  });
  const tiers = [...new Set(tiered.map((item) => valueOf(item, SIZE_TIER_ATTR)))];
  if (tiers.length < 2) return [];
  const offers: TierOffer[] = [];
  for (const group of sizeGroupsOf(tiered)) {
    const from = valueOf(group[0], SIZE_TIER_ATTR);
    const ids = new Set(group.map((item) => item.skuId));
    const shelves = shelvesOf(group);
    const within = (others: readonly StudioItem[]): boolean => {
      const run = shelvesOf(others);
      return (
        run.length > 0 && shelves.every((shelf) => shelf >= run[0] && shelf <= run[run.length - 1])
      );
    };
    const ofTier = (tier: string) =>
      tiered.filter((item) => !ids.has(item.skuId) && valueOf(item, SIZE_TIER_ATTR) === tier);
    const own = ofTier(from);
    // A tier that is only this pack size has no other run to stand apart from.
    if (own.length === 0 || within(own)) continue;
    // Standing with a tier's packs: inside that tier's run, and that tier is most of every shelf
    // the pack size stands on. Two tiers meeting on one shelf are neighbouring rows, not a tag
    // the base contradicts.
    const holdsShelves = (tier: string): boolean =>
      shelves.every((shelf) => {
        const onShelf = tiered.filter((item) =>
          item.placements.some((placement) => placement.shelf === shelf),
        );
        const host = onShelf.filter((item) => valueOf(item, SIZE_TIER_ATTR) === tier);
        return host.length * 2 > onShelf.length;
      });
    const hosts = tiers.filter(
      (tier) => tier !== from && within(ofTier(tier)) && holdsShelves(tier),
    );
    if (hosts.length !== 1) continue;
    // The tag is the borderline one: no other pack of the same form in its tier sits further
    // toward the host tier. Downy's 93 fl oz (Medium) on the Large shelf while a larger Medium
    // pack stays on the Medium shelf is a base that does not stack by size there.
    const size = packSizeOf(group[0]);
    const towardSmaller = sizeTierRank(hosts[0]) < sizeTierRank(from);
    const form = valueOf(group[0], FORM_ATTR);
    const ownSizes = own
      .filter((item) => valueOf(item, FORM_ATTR) === form)
      .map(packSizeOf)
      .filter((value): value is number => value !== null);
    if (size !== null && ownSizes.some((other) => (towardSmaller ? other < size : other > size)))
      continue;
    offers.push({
      skuIds: group.map((item) => item.skuId),
      size: valueOf(group[0], SIZE_ATTR),
      label: packSizeLabel(group[0]),
      name: group.length === 1 ? (group[0].name ?? null) : null,
      from,
      to: hosts[0],
      shelves,
      ownShelves: shelvesOf(own),
    });
  }
  return offers;
};

/** Every size-tier question of the brand blocks Studio reads as size rows. */
export const sizeTierFlags = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  { tierReadings, contract }: Pick<CollapseResult, "tierReadings" | "contract">,
): Flag[] => {
  const effective = items.map((item) => ({ ...item, attrs: effectiveAttrs(item, draft) }));
  const byColumn = new Map<string, StudioItem[]>();
  for (const item of effective) {
    const column = (contract.itemValues[item.skuId] ?? []).slice(0, 2).join(PATH_SEPARATOR);
    byColumn.set(column, [...(byColumn.get(column) ?? []), item]);
  }
  const byId = new Map<string, Flag>();
  for (const reading of tierReadings) {
    if (reading.cdt2?.orient !== "vertical") continue;
    const block = byColumn.get(reading.column) ?? [];
    // The size rows: the block's own, or each form row the base stacks by size.
    const rowSets =
      reading.cdt2.attr === SIZE_TIER_ATTR
        ? [block]
        : reading.cdt2.attr === FORM_ATTR
          ? [...new Set(block.map((item) => valueOf(item, FORM_ATTR)))]
              .map((form) => block.filter((item) => valueOf(item, FORM_ATTR) === form))
              .filter((row) => stackedBySize(row))
          : [];
    const scopePath = reading.column.split(PATH_SEPARATOR);
    const brand = (scopePath[scopePath.length - 1] ?? "").split(COMPOUND_JOIN)[0];
    for (const offer of rowSets.flatMap(offersIn)) {
      const id = sizeTierQuestionId(reading.column, offer.size, offer.to);
      const known = byId.get(id);
      const skuIds = [...(known?.skuIds ?? []), ...offer.skuIds];
      const count = skuIds.length;
      const lineWords = offer.label
        ? `${brand} ${offer.label}`
        : (offer.name ?? phrase("sizeTierLine", { brand, size: offer.size }));
      const line =
        typeof lineWords === "string" ? lineWords : phraseText(lineWords, englishFlagTemplate);
      const where = shelvesWords(offer.shelves);
      const fixLabel = phrase("sizeTierFix", { line: lineWords, to: offer.to });
      const words: FlagWords = {
        title: phrase("sizeTierTitle", { line: lineWords, from: offer.from, to: offer.to }, count),
        detail: [
          phrase("sizeTierDetail", {
            line: lineWords,
            brand,
            from: offer.from,
            to: offer.to,
            where,
            ownWhere: shelvesWords(offer.ownShelves),
          }),
        ],
        fixLabel,
      };
      byId.set(id, {
        id,
        kind: "data",
        severity: "blocking",
        scopePath,
        title: phraseText(words.title, englishFlagTemplate),
        detail: detailText(words, englishFlagTemplate),
        skuIds,
        anchorSkuId: skuIds[0],
        evidence: {
          notes: [
            `Size tier: ${offer.from}`,
            `On the base: ${phraseText(where, englishFlagTemplate)}`,
          ],
        },
        fix: { type: "retagItems", attr: SIZE_TIER_ATTR, value: offer.to, skuIds },
        fixLabel: phraseText(fixLabel, englishFlagTemplate),
        words,
        question: {
          brand,
          newBrand: brand,
          skuCount: count,
          where: [],
          mainWhere: [],
          sizeTier: {
            line,
            from: offer.from,
            to: offer.to,
            shelves: offer.shelves,
            ownShelves: offer.ownShelves,
          },
        },
      });
    }
  }
  return [...byId.values()];
};
